import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { effectiveTranscript, type Container, type FileEntryExplicit } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { log } from "../../logger";
import { useT } from "../../shared/i18n";
import { useAppStore } from "../../shared/store/app";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import {
  DeleteOutlineIcon,
  EditIcon,
  InfoOutlinedIcon,
  MicIcon,
  PhotoCameraIcon,
} from "../../shared/components/icons";
import { DetailPanel } from "../../shared/components/DetailPanel";
import {
  PhotoCard,
  PhotoCardCaption,
  type PhotoCardAction,
  type PhotoCardRelated,
} from "../../shared/components/PhotoCard";
import { mediaTarget, type ContainerMediaEntry } from "../../shared/lib/containerMedia";
import { MediaLightbox, type MediaLightboxPhoto } from "../gallery/MediaLightbox";
import { useDemoMode } from "../demo/useDemoMode";

/** A user-facing outcome from a media action, rendered by the caller's snackbar. */
export interface MediaNotice {
  message: string;
  severity: "success" | "error";
}

export type MediaNotify = (notice: MediaNotice) => void;

/** Stable identity of one media entry across containers. */
function mediaKey(entry: ContainerMediaEntry): string {
  return `${entry.container.id}:${entry.file.id}`;
}

/**
 * The container view's unified media gallery (container-view 02), optionally
 * merged with the descendant subtree's container files (container-media 02):
 * photos and records are equal cards in one grid — 3 columns on a phone, 4 from
 * `sm` up, the same geometry and gutter as the photo gallery.
 *
 * Every entry carries its owning container: previews, audio sources and every
 * write (remove, re-preview, re-transcribe, transcript edits) use that
 * container's id and version, never the viewed parent's. Descendant photos and
 * records also link to their owning container through the world-scoped opener;
 * the viewed container's own media has nowhere to go and shows no link. The
 * viewed container's own cards use the file caption variant (no redundant
 * title); descendant cards name their owning container above the file name.
 *
 * A photo uses the shared `PhotoCard`: the media opens the merged lightbox, the
 * info action opens the card's detail pane. A record is a card with a mic tile;
 * it opens the shared level-2 pane, where the player, the effective transcript
 * and the transcript/file actions live. The grid never expands a transcript
 * inline.
 */
export function ContainerMediaGrid({
  container,
  entries,
  subtreeEmptyNote = false,
  notify,
}: {
  /** The viewed container (the header already names it). */
  container: Container;
  /** The viewed container's files first, then matching descendant files. */
  entries: readonly ContainerMediaEntry[];
  /** Show the "no matching sub-container media" note next to the viewed media. */
  subtreeEmptyNote?: boolean;
  notify: MediaNotify;
}) {
  const t = useT();
  const demoMode = useDemoMode();
  const removeFile = useAppStore((s) => s.removeFile);
  const openContainer = useOpenContainer();
  const photos = entries.filter((entry) => entry.file.kind === "photo");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [recordKey, setRecordKey] = useState<string | null>(null);
  // Resolve the open record through the live entries: a re-transcribe or a
  // removal replaces the file entry, so a captured object would go stale.
  const recordEntry = recordKey
    ? (entries.find((entry) => mediaKey(entry) === recordKey) ?? null)
    : null;

  const openPhoto = (entry: ContainerMediaEntry) => setLightboxIndex(photos.indexOf(entry));

  /** Removing a file moves it to its owning container's trash (ADR-0008). */
  const handleRemove = async (entry: ContainerMediaEntry) => {
    const target = mediaTarget(entry);
    try {
      await removeFile(target.containerId, target.fileId, target.version);
      setRecordKey(null);
    } catch (err) {
      log.warn(
        { err, containerId: target.containerId, fileId: target.fileId },
        "Failed to remove file",
      );
      notify({ message: t("removeFileFailed"), severity: "error" });
    }
  };

  /**
   * The selected photo's actions. Every action targets that photo's owning
   * container: the descendant link (never the viewed parent), re-preview and
   * remove. The lightbox moved onto the panel image, so there is no open action.
   */
  const photoActions = (entry: ContainerMediaEntry): PhotoCardAction[] => {
    const target = mediaTarget(entry);
    return [
      ...(entry.descendant
        ? [
            {
              key: "container",
              label: t("goToContainer"),
              onClick: () => void openContainer(entry.container.id),
            },
          ]
        : []),
      ...(entry.file.data?.preview?.files?.length
        ? [
            {
              key: "repreview",
              label: t("rePreview"),
              icon: <PhotoCameraIcon fontSize="small" />,
              onClick: async () => {
                try {
                  await requests.media.rePreview(
                    target.containerId,
                    target.fileId,
                    target.version,
                  );
                  notify({ message: t("rePreviewed"), severity: "success" });
                } catch (err) {
                  log.warn(
                    { err, containerId: target.containerId, fileId: target.fileId },
                    "Re-preview file failed",
                  );
                  notify({ message: t("rePreviewFailed"), severity: "error" });
                }
              },
            },
          ]
        : []),
      {
        key: "remove",
        label: t("removeFile"),
        icon: <DeleteOutlineIcon fontSize="small" />,
        ariaLabel: `remove ${entry.file.name}`,
        onClick: () => void handleRemove(entry),
      },
    ];
  };

  /** Demo mode keeps the read-only navigation action, drops re-preview/remove. */
  const actionsFor = (entry: ContainerMediaEntry): PhotoCardAction[] =>
    demoMode
      ? photoActions(entry).filter((action) => action.key === "container")
      : photoActions(entry);

  /** Full panel data for one photo of the merged set. */
  const panelEntryOf = (entry: ContainerMediaEntry): PhotoCardRelated => {
    const target = mediaTarget(entry);
    return {
      key: mediaKey(entry),
      image: requests.media.previewUrl(target.containerId, target.fileId),
      alt: entry.file.name,
      title: entry.file.name,
      secondary: entry.container.name,
      identity: { uuid: entry.container.id, name: entry.container.name },
      actions: actionsFor(entry),
      onOpen: () => openPhoto(entry),
    };
  };

  if (entries.length === 0 && !subtreeEmptyNote) return null;

  return (
    <Box data-testid="container-media-grid" sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("files")}
      </Typography>
      <Grid container spacing={2}>
        {entries.map((entry) => {
          const target = mediaTarget(entry);
          return (
            <Grid
              item
              key={mediaKey(entry)}
              xs={4}
              sm={3}
              data-testid="container-media-tile"
            >
              {entry.file.kind === "photo" ? (
                <PhotoCard
                  image={requests.media.previewUrl(target.containerId, target.fileId)}
                  imageAlt={entry.file.name}
                  title={entry.container.name}
                  captionVariant={entry.descendant ? "title" : "file"}
                  secondary={entry.file.name}
                  panelTitle={entry.file.name}
                  panelSecondary={entry.container.name}
                  identity={{ uuid: entry.container.id, name: entry.container.name }}
                  related={photos
                    .filter((photo) => mediaKey(photo) !== mediaKey(entry))
                    .slice(0, 32)
                    .map(panelEntryOf)}
                  onOpen={() => openPhoto(entry)}
                  mediaLabel={`${t("openPhoto")} ${entry.file.name}`}
                  cardTestId="container-media-photo"
                  actions={actionsFor(entry)}
                />
              ) : (
                <RecordCard
                  container={entry.container}
                  file={entry.file}
                  descendant={entry.descendant}
                  onOpen={() => setRecordKey(mediaKey(entry))}
                />
              )}
            </Grid>
          );
        })}
      </Grid>

      {subtreeEmptyNote && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="container-subtree-media-empty"
          sx={{ mt: 1 }}
        >
          {t("noSubContainerMedia")}
        </Typography>
      )}

      <MediaLightbox
        open={lightboxIndex !== null && photos.length > 0}
        containerId={container.id}
        photos={photos.map<MediaLightboxPhoto>((entry) => ({
          ...entry.file,
          containerId: entry.container.id,
        }))}
        index={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      <DetailPanel
        open={recordEntry !== null}
        onClose={() => setRecordKey(null)}
        title={recordEntry?.file.name ?? ""}
        subtitle={
          <Typography variant="caption" color="text.secondary">
            {t("record")} · {recordEntry?.container.name}
          </Typography>
        }
        testId="record-transcript-pane"
      >
        {recordEntry && (
          <RecordPane
            entry={recordEntry}
            onRemove={() => void handleRemove(recordEntry)}
            notify={notify}
          />
        )}
      </DetailPanel>
    </Box>
  );
}

/**
 * The record tile: the photo card's shell with a mic instead of an image, so a
 * memo reads as one item in the same collection. The card and the info action
 * both open the level-2 transcript pane. A descendant card names its owning
 * container like the photo card; the viewed container's own card stays
 * title-free.
 */
function RecordCard({
  container,
  file,
  descendant,
  onOpen,
}: {
  container: Container;
  file: FileEntryExplicit;
  descendant: boolean;
  onOpen: () => void;
}) {
  const t = useT();
  return (
    <Box
      data-testid="container-media-record"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ position: "relative" }}>
        <ButtonBase
          onClick={onOpen}
          aria-label={`${t("showTranscript")} ${file.name}`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            aspectRatio: "1",
            bgcolor: "action.hover",
          }}
        >
          <Box sx={{ color: "text.disabled" }}>
            <MicIcon fontSize="large" />
          </Box>
        </ButtonBase>
        <IconButton
          size="small"
          data-testid="record-info"
          aria-label={`${t("details")} ${file.name}`}
          onClick={onOpen}
          sx={{
            position: "absolute",
            bottom: 6,
            right: 6,
            color: (theme) => alpha(theme.palette.common.white, 0.82),
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.16),
            "&:hover": {
              color: "common.white",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.32),
            },
            "&:focus-visible": {
              color: "common.white",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.32),
            },
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: { xs: 0.5, sm: 0.75 }, minWidth: 0 }}>
        <PhotoCardCaption
          variant={descendant ? "title" : "file"}
          title={container.name}
          secondary={file.name}
        />
      </Box>
    </Box>
  );
}

/**
 * Level-2 preview for one record: the player, the **effective** transcript
 * (manual override when present, otherwise the auto STT text), the manual/auto
 * state, and the transcript and file actions. `entry` always carries the
 * file's owning container; a descendant record also links to that container.
 */
function RecordPane({
  entry,
  onRemove,
  notify,
}: {
  entry: ContainerMediaEntry;
  onRemove: () => void;
  notify: MediaNotify;
}) {
  const t = useT();
  const readOnly = useDemoMode();
  const openContainer = useOpenContainer();
  const { file } = entry;
  const { containerId, fileId, version } = mediaTarget(entry);
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const sttText = file.data?.stt?.text;
  const effective = effectiveTranscript(file);
  const hasManual = effective?.manual ?? false;
  const transcript = effective?.text;
  const canShowOriginal = hasManual && sttText !== undefined;

  const openEdit = () => {
    setEditText(transcript ?? "");
    setEditOpen(true);
  };

  const saveTranscript = async () => {
    setSaving(true);
    try {
      await requests.media.setManualText(containerId, fileId, editText);
      notify({ message: t("transcriptSaved"), severity: "success" });
      setEditOpen(false);
    } catch (err) {
      log.warn({ err, containerId, fileId }, "Save manual transcript failed");
      notify({ message: t("transcriptSaveFailed"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const resetTranscript = async () => {
    setResetting(true);
    try {
      await requests.media.clearManualText(containerId, fileId);
      notify({ message: t("transcriptReset"), severity: "success" });
    } catch (err) {
      log.warn({ err, containerId, fileId }, "Reset manual transcript failed");
      notify({ message: t("transcriptResetFailed"), severity: "error" });
    } finally {
      setResetting(false);
    }
  };

  const reTranscribe = async () => {
    setTranscribing(true);
    try {
      await requests.media.reTranscribe(containerId, fileId, version);
      notify({ message: t("reTranscribed"), severity: "success" });
    } catch (err) {
      log.warn({ err, containerId, fileId }, "Re-transcribe file failed");
      notify({ message: t("reTranscribeFailed"), severity: "error" });
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        component="audio"
        controls
        preload="metadata"
        src={requests.media.fileUrl(containerId, fileId)}
        sx={{ width: "100%" }}
      />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("transcriptHeading")}
          </Typography>
          {hasManual && <Chip label={t("manualTranscript")} size="small" />}
        </Box>
        {transcript ? (
          <Typography
            variant="body2"
            data-testid="record-transcript"
            sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", fontSize: "0.875rem" }}
          >
            {transcript}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("noTranscript")}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {!readOnly && (
          <>
            <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={openEdit}>
              {t("editTranscript")}
            </Button>
            <Button
              size="small"
              startIcon={<MicIcon fontSize="small" />}
              disabled={transcribing}
              onClick={() => void reTranscribe()}
            >
              {t("reTranscribe")}
            </Button>
            {hasManual && (
              <Button
                size="small"
                color="inherit"
                disabled={resetting}
                onClick={() => void resetTranscript()}
              >
                {t("resetTranscript")}
              </Button>
            )}
          </>
        )}
        {entry.descendant && (
          <Button size="small" onClick={() => void openContainer(entry.container.id)}>
            {t("goToContainer")}
          </Button>
        )}
        {!readOnly && (
          <Button
            size="small"
            color="inherit"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={onRemove}
          >
            {t("removeFile")}
          </Button>
        )}
      </Stack>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("editTranscriptTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            margin="dense"
            label={t("transcriptField")}
            helperText={t("transcriptEmptyHint")}
            value={editText}
            onChange={(event) => setEditText(event.target.value)}
          />
          {canShowOriginal && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary">
                {t("originalTranscript")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.875rem",
                  color: "text.secondary",
                  maxHeight: 160,
                  overflow: "auto",
                }}
              >
                {sttText}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button variant="contained" onClick={() => void saveTranscript()} disabled={saving}>
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
